import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, RadioGroup, Radio } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import _ from 'lodash';
import MpiFourWheelInput from '../MpiFourWheelInput';

const DynamicColumns = ({ dynamicHeaders, handleCellEdit, handleInputChange, handleInputCommit, canEditField, getUserStamp, work_item_presets, setError, inputValues }) => {
  const hardcodedColors = {
    pass: '#d4edda',
    recommend: '#fff3cd',
    required: '#f8d7da',
    approved: '#d1ecf1',
    completed: '#d4edda',
  };

  const getCellStyle = (field, value) => {
    if (value !== true && value !== 'true') return {};

    const header = dynamicHeaders.find(h => h.field === field);
    const color = header?.true_color || hardcodedColors[field];

    return color ? { backgroundColor: color } : {};
  };

  const getInputType = (field, item) => {
    const presets = Array.isArray(work_item_presets) ? work_item_presets : [];
    const preset = _.find(presets, p => p.id === item.work_item_preset_id) || {};
    const header = dynamicHeaders.find(h => h.field === field);

    let inputType = preset[`${field}_inputtype`] || header?.inputtype;

    // Fallback logic: If no explicit inputtype, infer from value/data
    if (!inputType) {
      const value = item[field];
      const hasStamp = item[`${field}_by_uid`] || item[`${field}_by_date`];
      if (value === 'true' || value === 'false' || typeof value === 'boolean' || value === undefined || value === '') {
        inputType = hasStamp ? 'userstamp' : 'checkbox';
      } else if (typeof value === 'string' && value.trim()) {
        inputType = 'text';
      } else {
        inputType = 'text'; // Catch-all
      }
    }

    if (booleanFields.includes(field) || inputType === 'userstamp') {
      return 'checkbox';
    }

    return inputType === 'textstamp' ? 'text' : inputType;
  };

  const booleanFields = [
    'show_mpi', 'active', 'deleted', 'auto_assign', 'pass', 'recommend', 'required',
    'approved', 'declined', 'completed', 'dvi_pdf_show', 'lock_pass', 'lock_recommend',
    'lock_required', 'lock_labor_rate', 'lock_labor_hours', 'lock_labor_total',
    'lock_parts', 'lock_parts_total', 'lock_quantity', 'lock_estimate_total',
    'lock_approved', 'lock_declined', 'lock_completed', 'lock_estimate', 'lock_counter',
  ];

  const isNumericField = (field) => ['labor_hours', 'labor_rate', 'quantity', 'parts', 'estimate_total', 'parts_total', 'labor_total'].includes(field);

  return dynamicHeaders.map(h => ({
    accessorKey: h.field,
    header: h.label,
    size: 100,
    muiTableBodyCellProps: ({ cell }) => ({
      sx: getCellStyle(h.field, cell.getValue()),
    }),
    enableEditing: true,
    muiEditTextFieldProps: ({ cell, row }) => {
      const field = h.field;
      const item = row.original || {};
      const editable = canEditField(item, field, h.allowed_profiles || '');
      const inputType = getInputType(field, item);
      const options = item[`${field}_options`] ? item[`${field}_options`].split(',').filter(Boolean) : [];

      if (field === 'name' && item.layout_type === '4_wheel_input') {
        return { disabled: true };
      }

      if (inputType === 'checkbox') {
        return {
          disabled: !editable,
        };
      } else if (inputType === 'select') {
        return {
          disabled: !editable,
          select: true,
          children: options.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          )),
          value: inputValues[`${row.original.id}-${field}`] ?? cell.getValue() ?? '',
          onChange: (event) => handleInputChange(row.original.id, field, event.target.value),
          onBlur: (event) => handleInputCommit(row, { id: field }, event.target.value),
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              handleInputCommit(row, { id: field }, event.target.value);
            }
          },
        };
      } else if (inputType === 'radio') {
        return {
          disabled: !editable,
        };
      } else {
        return {
          disabled: !editable,
          multiline: inputType === 'textstamp',
          rows: inputType === 'textstamp' ? 2 : 1,
          type: isNumericField(field) ? 'number' : 'text',
          value: inputValues[`${row.original.id}-${field}`] ?? cell.getValue() ?? '',
          onChange: (event) => handleInputChange(row.original.id, field, event.target.value),
          onBlur: (event) => handleInputCommit(row, { id: field }, event.target.value),
          onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              handleInputCommit(row, { id: field }, event.target.value);
            }
          },
        };
      }
    },
    Cell: ({ cell, row }) => {
      const field = h.field;
      const value = inputValues[`${row.original.id}-${field}`] ?? cell.getValue();
      const item = row.original || {};
      const editable = canEditField(item, field, h.allowed_profiles || '');
      const inputType = getInputType(field, item);
      const options = item[`${field}_options`] ? item[`${field}_options`].split(',').filter(Boolean) : [];
      const isUserStamp = h.inputtype === 'userstamp' || inputType === 'userstamp';
      const showUserStamp = isUserStamp || h.inputtype === 'textstamp';
      const isCustomHeader = field.startsWith('custom_header_');

      if (field === 'name' && item.layout_type === '4_wheel_input') {
        return (
          <Box sx={{ padding: '0.5rem' }}>
            <MpiFourWheelInput
              item={item}
              handleCellEdit={handleCellEdit}
              canEditField={canEditField}
              inputValues={inputValues}
              handleInputChange={handleInputChange}
              handleInputCommit={handleInputCommit}
            />
          </Box>
        );
      }

      if (!editable && inputType !== 'checkbox') {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>{value ?? 'N/A'}</Typography>
            {item[`${field}_locked`] && <LockIcon fontSize="small" color="disabled" />}
            {showUserStamp && getUserStamp(item, field)}
          </Box>
        );
      }

      if (inputType === 'checkbox') {
        const checkboxValue = isCustomHeader && isUserStamp
          ? value === 'true'
          : value === true || value === 'true';

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkboxValue}
                  onChange={(e) => {
                    try {
                      handleCellEdit(row, { id: field }, e.target.checked);
                    } catch (error) {
                      if (typeof setError === 'function') {
                        setError(`Failed to update ${field}: ${error.message}`);
                      }
                    }
                  }}
                  disabled={!editable}
                  sx={{ p: 0 }} // Reduce padding if needed for tighter centering
                />
              }
              label=""
              sx={{ m: 0, display: 'flex', justifyContent: 'center' }} // Center the label/checkbox
            />
            {showUserStamp && checkboxValue && (
              <Typography variant="caption" sx={{ textAlign: 'center', mt: 0.5 }}>
                {getUserStamp(item, field)}
              </Typography>
            )}
          </Box>
        );
      } else if (inputType === 'radio') {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <RadioGroup
              row
              value={value ?? ''}
              onChange={(e) => {
                try {
                  handleInputChange(row.original.id, field, e.target.value);
                } catch (error) {
                  if (typeof setError === 'function') {
                    setError(`Failed to update ${field}: ${error.message}`);
                  }
                }
              }}
              onBlur={(e) => {
                try {
                  handleInputCommit(row, { id: field }, e.target.value);
                } catch (error) {
                  if (typeof setError === 'function') {
                    setError(`Failed to update ${field}: ${error.message}`);
                  }
                }
              }}
              disabled={!editable}
            >
              {options.map(opt => (
                <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
              ))}
            </RadioGroup>
            {showUserStamp && getUserStamp(item, field)}
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>{value ?? 'N/A'}</Typography>
          {showUserStamp && getUserStamp(item, field)}
        </Box>
      );
    },
  }));
};

export default DynamicColumns;